/**
 * Check if an error is a CORS error
 */
export const isCorsError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('cors') ||
      message.includes('access-control') ||
      message.includes('failed to fetch') ||
      message.includes('networkerror')
    );
  }
  return false;
};

/**
 * CORS error retry configuration
 */
export interface CorsRetryConfig {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
}

/**
 * Default CORS retry configuration
 */
const DEFAULT_CORS_RETRY_CONFIG: Required<CorsRetryConfig> = {
  maxRetries: 5,
  baseDelayMs: 1000,
  maxDelayMs: 30000
};

/**
 * Calculate exponential backoff delay
 */
export const calculateBackoffDelay = (retryCount: number, config: CorsRetryConfig = {}): number => {
  const { baseDelayMs, maxDelayMs } = { ...DEFAULT_CORS_RETRY_CONFIG, ...config };
  return Math.min(maxDelayMs, baseDelayMs * Math.pow(2, retryCount));
};

/**
 * Check if we should retry after a CORS error
 */
export const shouldRetryCorsError = (
  retryCount: number,
  lastRetryTime: number,
  config: CorsRetryConfig = {}
): { shouldRetry: boolean; delayMs: number } => {
  const { maxRetries } = { ...DEFAULT_CORS_RETRY_CONFIG, ...config };

  if (retryCount > maxRetries) {
    return { shouldRetry: false, delayMs: 0 };
  }

  const now = Date.now();
  const timeSinceLastRetry = now - lastRetryTime;
  const backoffDelay = calculateBackoffDelay(retryCount, config);

  if (timeSinceLastRetry < backoffDelay) {
    return { shouldRetry: false, delayMs: backoffDelay - timeSinceLastRetry };
  }

  return { shouldRetry: true, delayMs: 0 };
};
